import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { useCollection } from "../../hooks/useCollection";
import {
  createStaffAccount,
  getStaffUsers,
  setStaffRole,
} from "../../services/dataService";
import { logUserActivity } from "../../utils/activityLogger";

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

// The five values setStaffRole accepts (functions/setStaffRole.js): the four staff
// roles permissions.js names, plus "user" — remove someone from staff entirely.
const ROLE_OPTIONS = [
  { value: "superAdmin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "moderator", label: "Moderator" },
  { value: "accountant", label: "Accountant" },
  { value: "user", label: "Remove from staff" },
];
const roleLabel = (value) =>
  (ROLE_OPTIONS.find((o) => o.value === value) || { label: value }).label;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Roles the caller may hand out with Add staff (spec 044 D62): a superAdmin may add
// any staff role; an admin only Moderator and Accountant. "Remove from staff" never
// appears here — that is a role change, not something you add someone as.
const addableRoles = (callerRole) =>
  callerRole === "superAdmin"
    ? ROLE_OPTIONS.filter((o) => o.value !== "user")
    : ROLE_OPTIONS.filter((o) => o.value === "moderator" || o.value === "accountant");

/**
 * Staff accounts (spec 044 D11/D12) — the card that lived in Settings, now its
 * own page with its own drawer entry. Roles of accounts that already exist, plus
 * Add staff (D11/D62–D66): new accounts are created here instead of the Firebase
 * console. Per-person grants and disabling an account arrive with D11's own slice.
 */
const Staff = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";
  const canManageStaff = hasPermission(userRole, permissions.MANAGE_STAFF);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });
  const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  const staffFetcher = useCallback(
    () => (canManageStaff ? getStaffUsers() : Promise.resolve([])),
    [canManageStaff]
  );
  const {
    data: staff,
    loading: staffLoading,
    error: staffError,
    refetch: refetchStaff,
  } = useCollection(staffFetcher);
  // { person, nextRole } while the confirm dialog is up; null otherwise.
  const [pending, setPending] = useState(null);
  const [submittingRole, setSubmittingRole] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPassword, setAddPassword] = useState("");
  const [addShowPassword, setAddShowPassword] = useState(false);
  const [addRole, setAddRole] = useState("");
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState("");

  const resetAddForm = () => {
    setAddName("");
    setAddEmail("");
    setAddPassword("");
    setAddShowPassword(false);
    setAddRole("");
    setAddError("");
  };
  const closeAddDialog = () => {
    if (addSubmitting) return;
    setAddOpen(false);
    resetAddForm();
  };
  const addNameValid = addName.trim().length > 0;
  const addEmailValid = EMAIL_RE.test(addEmail.trim());
  const addPasswordValid = addPassword.length >= 8;
  const addRoleValid = Boolean(addRole);
  const addFormValid = addNameValid && addEmailValid && addPasswordValid && addRoleValid;

  const submitAddStaff = async () => {
    if (!addFormValid) return;
    setAddSubmitting(true);
    setAddError("");
    try {
      const name = addName.trim();
      const created = await createStaffAccount({
        name,
        email: addEmail.trim(),
        password: addPassword,
        role: addRole,
      });
      setAddOpen(false);
      resetAddForm();
      notify(`Added ${name} as ${roleLabel(created.role || addRole)}. Give them the password privately.`);
      await refetchStaff();
    } catch (e) {
      setAddError(e.message || "Could not add the staff member.");
    } finally {
      setAddSubmitting(false);
    }
  };

  const confirmRoleChange = async () => {
    if (!pending) return;
    const { person, nextRole } = pending;
    const personName = person.name || person.id;
    setSubmittingRole(true);
    try {
      await setStaffRole({ userId: person.id, role: nextRole });
      await logUserActivity({
        userId: user?.uid || user?.id || "unknown",
        userName: user?.name || user?.displayName || "Unknown User",
        userEmail: user?.email || "unknown@example.com",
        action: "STAFF_ROLE_CHANGE",
        entityType: "user",
        entityId: person.id,
        entityName: personName,
        before: { role: person.role },
        after: { role: nextRole },
        description: `${user?.name || "User"} changed ${personName}'s role from ${roleLabel(
          person.role
        )} to ${roleLabel(nextRole)}`,
      });
      notify(`Changed ${personName}'s role to ${roleLabel(nextRole)}.`);
      setPending(null);
      await refetchStaff();
    } catch (e) {
      // The dialog stays open: the admin sees exactly what was refused (e.g. the
      // last-superAdmin guard) against the change they were confirming.
      notify(e.message || "Failed to change the role.", "error");
    } finally {
      setSubmittingRole(false);
    }
  };

  const pendingName = pending ? pending.person.name || pending.person.id : "";

  const header = (
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Admin
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
        Staff
      </Typography>
    </Box>
  );

  // The route already gates this page on MANAGE_STAFF; this is the same check the
  // Settings card made, kept so the page never renders staff rows to someone who
  // reached it another way.
  if (!canManageStaff) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        {header}
        <Alert severity="warning">
          You do not have access to staff accounts.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {header}

      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Everyone with dashboard access. Add staff here and change roles in
              the list.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setAddOpen(true)}
            >
              Add staff
            </Button>
          </Box>
          {staffError && <Alert severity="error">{staffError}</Alert>}
          {staffLoading ? (
            <CircularProgress size={24} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staff.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.name || row.id}</TableCell>
                    <TableCell>{row.email || "—"}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.role}
                        disabled={submittingRole}
                        onChange={(e) => {
                          const nextRole = e.target.value;
                          if (nextRole === row.role) return;
                          setPending({ person: row, nextRole });
                        }}
                      >
                        {ROLE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
                {staff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary">
                        No staff accounts found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!pending}
        onClose={() => {
          if (!submittingRole) setPending(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Change {pendingName}'s role?
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {pending &&
              `${pendingName}: ${roleLabel(pending.person.role)} → ${roleLabel(
                pending.nextRole
              )}.`}
            {pending?.nextRole === "user" &&
              " They will lose all dashboard access."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPending(null)}
            disabled={submittingRole}
            sx={actionButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending?.nextRole === "user" ? "error" : "primary"}
            disabled={submittingRole}
            onClick={confirmRoleChange}
            sx={actionButtonSx}
          >
            {submittingRole ? "Changing..." : "Change role"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>Add staff</DialogTitle>
        <DialogContent dividers>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            required
            autoFocus
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            required
            value={addEmail}
            onChange={(e) => setAddEmail(e.target.value)}
          />
          <TextField
            label="Password"
            type={addShowPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            required
            autoComplete="new-password"
            value={addPassword}
            onChange={(e) => setAddPassword(e.target.value)}
            helperText="At least 8 characters. Give it to them privately."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={addShowPassword ? "Hide password" : "Show password"}
                    onClick={() => setAddShowPassword((s) => !s)}
                    edge="end"
                  >
                    {addShowPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Role"
            fullWidth
            margin="normal"
            required
            value={addRole}
            onChange={(e) => setAddRole(e.target.value)}
          >
            {addableRoles(userRole).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog} disabled={addSubmitting} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={addSubmitting || !addFormValid}
            onClick={submitAddStaff}
            sx={actionButtonSx}
          >
            {addSubmitting ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Staff;
